import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { SearchCombobox } from './SearchCombobox';

const COMMODITIES = ['electricity', 'gas', 'water', 'sewer', 'stormwater', 'trash', 'internet', 'other'];

const Field = ({ label, children, className = '' }) => (
  <div className={className}><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

const MetersPicker = ({ row, st, set, i }) => {
  if (!row.meters_applicable) return <p className="text-[11px] text-muted-foreground">No meters for this commodity — none will be added.</p>;
  if (!row.meters.length) return <p className="text-[11px] text-muted-foreground">No meter numbers on the bill for this account.</p>;
  const toggle = (n) => set({ meter_numbers: st.meter_numbers.includes(n) ? st.meter_numbers.filter(x => x !== n) : [...st.meter_numbers, n] });
  return (
    <div className="flex flex-wrap gap-3">
      {row.meters.map(m => (
        <label key={m.number} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`obl-row-${i}-meter-${m.number}`}>
          <Checkbox checked={st.meter_numbers.includes(m.number)} onCheckedChange={() => toggle(m.number)} />
          <span className="font-mono">{m.number}</span>
          <Badge variant="outline" className={`text-[9px] ${m.on_account ? 'text-muted-foreground' : m.exists ? 'text-sky-600 border-sky-400/50' : 'text-emerald-600 border-emerald-400/50'}`}>
            {m.on_account ? 'on account' : m.exists ? 'existing' : 'new'}
          </Badge>
        </label>
      ))}
    </div>
  );
};

const CreateFields = ({ row, st, set, i, vendors, properties }) => {
  const commodityOpts = Array.from(new Set([...(st.commodity ? [st.commodity] : []), ...COMMODITIES]));
  const np = st.new_property;
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Account number *">
        <Input className="h-8 text-xs font-mono" value={st.account_number} onChange={e => set({ account_number: e.target.value })} data-testid={`obl-row-${i}-acct-number`} />
      </Field>
      <Field label="Commodity">
        <Select value={st.commodity} onValueChange={v => set({ commodity: v })}>
          <SelectTrigger className="h-8 text-xs capitalize" data-testid={`obl-row-${i}-commodity`}><SelectValue /></SelectTrigger>
          <SelectContent>{commodityOpts.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label={st.new_vendor_name ? 'Vendor (new)' : 'Vendor'}>
        <SearchCombobox items={vendors} value={st.vendor_id} placeholder="Select vendor" testid={`obl-row-${i}-vendor`}
          display={st.new_vendor_name ? `+ ${st.new_vendor_name}` : ''}
          onChange={v => set({ vendor_id: v, new_vendor_name: '' })}
          onCreate={q => set({ vendor_id: '', new_vendor_name: q || row.vendor_name || '' })} createLabel="Create vendor" />
      </Field>
      <Field label={np ? 'Property (new)' : 'Property *'}>
        <SearchCombobox items={properties} value={st.linked_location_id} placeholder="Select property" testid={`obl-row-${i}-property`}
          display={np ? `+ ${np.name || 'New property'}` : ''}
          onChange={v => set({ linked_location_id: v, new_property: null })}
          onCreate={q => set({ linked_location_id: '', new_property: { name: q || row.service_address || '', address: row.service_address || '', city: row.service_city || '', state: row.service_state || '', zip: row.service_zip || '' } })}
          createLabel="Create property" />
      </Field>
      {np && (
        <div className="col-span-2 grid grid-cols-6 gap-2 rounded border border-dashed p-2 bg-muted/20" data-testid={`obl-row-${i}-new-property`}>
          <Field label="Property name" className="col-span-3"><Input className="h-7 text-xs" value={np.name} onChange={e => set({ new_property: { ...np, name: e.target.value } })} data-testid={`obl-row-${i}-np-name`} /></Field>
          <Field label="Address" className="col-span-3"><Input className="h-7 text-xs" value={np.address} onChange={e => set({ new_property: { ...np, address: e.target.value } })} /></Field>
          <Field label="City" className="col-span-2"><Input className="h-7 text-xs" value={np.city} onChange={e => set({ new_property: { ...np, city: e.target.value } })} /></Field>
          <Field label="State" className="col-span-2"><Input className="h-7 text-xs" value={np.state} onChange={e => set({ new_property: { ...np, state: e.target.value } })} /></Field>
          <Field label="Zip" className="col-span-2"><Input className="h-7 text-xs" value={np.zip} onChange={e => set({ new_property: { ...np, zip: e.target.value } })} /></Field>
        </div>
      )}
      <Field label="Service address" className="col-span-2">
        <Input className="h-8 text-xs" value={st.service_address} onChange={e => set({ service_address: e.target.value })} data-testid={`obl-row-${i}-address`} />
      </Field>
    </div>
  );
};

export const ResolveRow = ({ row, st, set, i, vendors, properties }) => {
  const options = [...(row.match ? [row.match] : []), ...row.candidates.filter(c => c.id !== row.match?.id)];
  return (
    <div className={`border rounded-lg p-3 space-y-3 ${st.mode === 'skip' ? 'opacity-50' : ''}`} data-testid={`obl-resolve-row-${i}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-sm font-medium">{row.account_number || 'No account #'}</span>
        <span className="text-[11px] text-muted-foreground capitalize">{row.commodity || '—'} · {row.service_address || 'no address'}</span>
        {row.match
          ? <Badge variant="outline" className="text-[10px] text-sky-600 border-sky-400/50" data-testid={`obl-row-${i}-existing`}>Existing account</Badge>
          : <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-400/50" data-testid={`obl-row-${i}-new`}>New account</Badge>}
        {!row.is_primary_bill && <Badge variant="secondary" className="text-[10px]" title={row.filename}>Sibling bill</Badge>}
        <div className="ml-auto w-44">
          <Select value={st.mode} onValueChange={v => set({ mode: v, acct_id: v === 'link' ? (st.acct_id || options[0]?.id || '') : st.acct_id })}>
            <SelectTrigger className="h-7 text-xs" data-testid={`obl-row-${i}-mode`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="link" disabled={!options.length}>Link to existing</SelectItem>
              <SelectItem value="create">Create new account</SelectItem>
              <SelectItem value="skip">Skip</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {st.mode === 'link' && (
        <Select value={st.acct_id} onValueChange={v => set({ acct_id: v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`obl-row-${i}-link-target`}><SelectValue placeholder="Choose account" /></SelectTrigger>
          <SelectContent>
            {options.map(c => (
              <SelectItem key={c.id} value={c.id}>
                <span className="font-mono">{c.account_number}</span> · {c.property_name || 'No property'}{c.score ? ` · ${c.score}%` : ' · exact'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {st.mode === 'create' && <CreateFields row={row} st={st} set={set} i={i} vendors={vendors} properties={properties} />}
      {st.mode !== 'skip' && (
        <div><Label className="text-[11px] text-muted-foreground">Meters from the bill</Label><MetersPicker row={row} st={st} set={set} i={i} /></div>
      )}
    </div>
  );
};
